// The sky over the overworld chart, as layers over the terrain canvas (docs/24 K5, docs/19 at chart
// scale, docs/23 Y8): the light, the weather on the land, what falls, the season's particles. Drawn
// by the World screen over its chart and by the Art Studio over its sample (docs/70); the CSS is in
// `world.css`. `worldSky` and `owLight` (worldSky.ts) say what to draw.
import type { CSSProperties } from 'react';
import { useSettings } from '../../../core/stores/settings';
import type { OwLight, WorldSky } from './worldSky';

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

/**
 * The week's weather on the land (docs/24 K5), under the place nodes: cloud shadows sailing
 * across, fog banks, rime at the edges, a heat glare and the season's tint. Transform-only
 * animation (a full-window background animation made swiftshader unscreenshottable, docs/23 §8).
 */
export function LandSky({ sky }: { sky: WorldSky }) {
  return (
    <div className="fk-ow-land" aria-hidden="true" data-testid="world-land-sky" data-clouds={sky.clouds} data-fog={sky.fog} data-ice={sky.ice}>
      {sky.tint && <div className="fk-ow-land__tint" style={{ background: sky.tint }} />}
      {range(sky.clouds).map((i) => (
        <i key={`c${i}`} className="fk-ow-cloud" style={{ top: `${(i * 29 + 7) % 92}%`, animationDuration: `${70 + (i % 4) * 22}s`, animationDelay: `${-((i * 37) % 90)}s`, ['--fk-alpha' as string]: sky.cloudAlpha, ['--fk-w' as string]: `${32 + (i % 3) * 14}%` } as CSSProperties} />
      ))}
      {sky.fog > 0 && range(Math.round(4 + sky.fog * 6)).map((i) => (
        <i key={`f${i}`} className="fk-ow-fog" style={{ top: `${(i * 23 + 4) % 90}%`, animationDuration: `${90 + (i % 5) * 25}s`, animationDelay: `${-((i * 41) % 120)}s`, ['--fk-alpha' as string]: 0.2 + 0.32 * sky.fog } as CSSProperties} />
      ))}
      {sky.frost && <div className="fk-ow-land__frost" />}
      {sky.heat && <div className="fk-ow-land__heat" />}
    </div>
  );
}

/**
 * The week's light on the land (docs/19 at chart scale), between the terrain and the nodes: the
 * hour's colour multiplied over everything, the sun's warmth on its side of the valley with a glow
 * where it stands, and cold shade pooling on the far side. Each layer sits straight on the stage so
 * it blends with the canvas under it; the canvas itself carries the props' shadows (`MapCanvas` `cast`).
 */
export function LandLight({ light, soft }: { light: OwLight; soft: number }) {
  return (
    <>
      <div className="fk-ow-light fk-ow-light--ambient" style={{ background: light.ambient, opacity: soft }} />
      <div className="fk-ow-light fk-ow-light--shade" style={{ background: `linear-gradient(${light.angle}deg, transparent 30%, rgba(18, 24, 60, ${light.shadeAlpha * soft}) 100%)` }} />
      <div className="fk-ow-light fk-ow-light--sun" data-testid="world-light" data-phase={light.phase} style={{ background: `radial-gradient(70% 80% at ${light.glow.x}% ${light.glow.y}%, ${light.sun}, transparent 75%), linear-gradient(${light.angle}deg, ${light.sun}, transparent 60%)`, opacity: light.sunAlpha * soft }} />
    </>
  );
}

/**
 * What falls over the chart (docs/24 K5): rain, sleet, snow or blown sand. Showers and snow come
 * in two fronts that drift across the valley, so some of the map is dry at any moment; a storm,
 * a blizzard or a sandstorm covers it all. Storms flash now and then (off with the lightning
 * setting or reduce motion).
 */
export function WeatherSky({ sky }: { sky: WorldSky }) {
  const flashes = useSettings((s) => s.lightningFlashes && !s.reduceMotion);
  if (!sky.precip && !sky.lightning) return null;
  const kind = sky.precip ?? 'rain';
  const fall = (n: number, key: string) => range(n).map((i) => (
    <i key={`${key}${i}`} style={{ left: `${(i * 37 + (i % 7) * 11) % 100}%`, animationDelay: `${-((i * 0.61) % 14)}s`, animationDuration: `${(kind === 'rain' ? 0.8 : kind === 'sleet' ? 1.1 : kind === 'sand' ? 2.4 : 7) * (1 - 0.45 * sky.wind) + (i % 5) * (kind === 'snow' ? 1.1 : 0.12)}s`, ['--fk-sway' as string]: `${((i % 9) - 4) * 9}px`, ['--fk-size' as string]: `${2 + (i % 3)}px` } as CSSProperties} />
  ));
  return (
    <div className={`fk-ow-sky fk-ow-sky--${kind}`} aria-hidden="true" data-testid="world-weather" data-weather={sky.weather} data-precip={sky.precip ?? ''} data-fronts={sky.fronts ? 'true' : 'false'} style={{ ['--fk-wind' as string]: sky.wind } as CSSProperties}>
      {sky.precip && (sky.fronts
        ? [0, 1].map((f) => <div key={f} className="fk-ow-front" style={{ animationDuration: `${110 + f * 40}s`, animationDelay: `${-f * 70}s` }}>{fall(Math.round(sky.precipCount * 0.6), `p${f}-`)}</div>)
        : <div className="fk-ow-front fk-ow-front--all">{fall(sky.precipCount, 'p')}</div>)}
      {sky.lightning && flashes && <div className="fk-ow-sky__flash" data-testid="world-lightning" />}
    </div>
  );
}

/**
 * The season over the chart (docs/23 Y8): drifting blossom in spring, pollen motes in summer (a
 * heat shimmer over the desert), falling leaves in autumn, snow in winter (sand in the desert, rain
 * in the jungle; embers or falling ash in the Ashlands), when the week's weather leaves room for it (`worldSky`). Decoration only: CSS
 * particles above the map and under the panels, still under reduce motion.
 */
export function SeasonSky({ kind }: { kind: string }) {
  const n = kind === 'shimmer' ? 0 : kind === 'rain' ? 60 : kind === 'snow' || kind === 'ash' ? 70 : 34;
  return (
    <div className={`fk-world__sky fk-world__sky--${kind}`} aria-hidden="true" data-testid="season-sky" data-kind={kind}>
      {Array.from({ length: n }, (_, i) => (
        <i key={i} style={{ left: `${(i * 37 + (i % 7) * 11) % 100}%`, animationDelay: `${-((i * 0.61) % 14)}s`, animationDuration: `${(kind === 'rain' ? 0.9 : kind === 'snow' || kind === 'ash' ? 9 : 13) + (i % 5) * (kind === 'rain' ? 0.12 : 1.3)}s`, ['--fk-sway' as string]: `${((i % 9) - 4) * 9}px`, ['--fk-size' as string]: `${2 + (i % 3)}px` } as CSSProperties} />
      ))}
    </div>
  );
}
